/**
 * `useUpdatePublishedProject` mutation hook.
 *
 * Wraps the entity-level `updateProject` raw service fn: mutation
 * hooks live in features and compose entity service fns.
 *
 * Cache invalidation (per the cache-key contract):
 *   - `PROJECTS_QUERY_KEYS.detail(projectId)` — current project view
 *   - `PROJECTS_QUERY_KEYS.list` (prefix) — every paginated workspace list
 *   - `PROJECTS_QUERY_KEYS.filterOptions(workspaceId)` — distinct lists, when
 *     workspaceId is provided
 *   - `['projects', 'sidebar', workspaceId]` — sidebar nav list, when
 *     workspaceId is provided (key shape per `PROJECTS_QUERY_KEYS.sidebar`)
 *
 * Side effects: NONE. Toast messaging lives in the caller (the edit form
 * needs to also handle the 409 conflict case with a distinct toast string,
 * which is form-specific UX, not a generic mutation concern).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateProjectDto } from '@inference.engine/workspace-api-client/web';
import { PROJECTS_QUERY_KEYS, updateProject } from '@/entities/project';

export { updateProject };

interface UseUpdateProjectOptions {
  /**
   * Workspace ID — when provided, also invalidates the workspace-scoped
   * sidebar + filter-options queries. Optional because some callers (e.g.
   * automated/test fixtures) don't have a workspace context.
   */
  workspaceId?: string | null;
}

interface UpdateProjectVariables {
  projectId: string;
  payload: UpdateProjectDto;
}

export function useUpdatePublishedProject(options: UseUpdateProjectOptions = {}) {
  const queryClient = useQueryClient();
  const { workspaceId } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }: UpdateProjectVariables) =>
      updateProject(projectId, payload),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: PROJECTS_QUERY_KEYS.detail(projectId),
      });
      // Invalidates BOTH projectsListQuery (flat) AND projectsInfiniteQuery
      // (infinite scroll) via prefix matching. The 'infinite' variant key is
      // ['projects', 'list', 'infinite', wsId, query], so this prefix catches it.
      void queryClient.invalidateQueries({
        queryKey: ['projects', 'list'],
      });

      if (workspaceId) {
        // sidebar prefix `['projects', 'sidebar', wsId]` matches every search
        // variant (`['projects', 'sidebar', wsId, search]`) per TanStack
        // prefix semantics. Mirrors the legacy useEditProjectForm shape.
        void queryClient.invalidateQueries({
          queryKey: ['projects', 'sidebar', workspaceId],
        });
        void queryClient.invalidateQueries({
          queryKey: PROJECTS_QUERY_KEYS.filterOptions(workspaceId),
        });
      }
    },
  });
}
